import React, { useEffect, useRef, useState } from 'react';
import { Stage, Group, Layer, Rect, Circle, Ellipse, Star, RegularPolygon, Text, Transformer, Image as KonvaImage, Line } from 'react-konva';
import useDesignerStore from './designerStore';
import PhotoFrame from './PhotoFrame';
import { Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import QRCode from 'qrcode';
import {loadFonts} from '../../services/fontService';
import Konva from 'konva';
import { cardSizes, getStageDimensions } from './cardSizes';
import { getDirectionPoints } from './getDirectionPoints'; // adjust path as needed
import { convertHexToRGBA } from '../constants.js';
import useImage from 'use-image';
import BoundImage from './BoundImage';
import ImageShape from './ImageShape';
import CanvasImage from './CanvasImage'; // Adjust path if needed
import { calculateFontSizeToFitBox  } from './utils/textHelpers'; // adjust path
import { fitDesignerScale } from './designerViewport';
import { getTextOrigin } from '../../rendering/konvaCardRenderer';

import {
  ellipseToPolygonPoints
} from './utils/shapeGeometry';



const useCachedImage = (src) => {
  const [image] = useImage(src, 'anonymous');
  return { image };
};

const qrCache = new Map();

const buildQrCacheKey = (obj, activeSurface) =>
  `${obj.qrValue}-${obj.qrColor}-${obj.qrBackground}-${obj.qrQuietZone}-${activeSurface}`;



const getQrImage = async (key, value, color) => {
  if (qrCache.has(key)) return qrCache.get(key);

  const dataUrl = await QRCode.toDataURL(value || ' ', {
    margin: 0,
    color: {
      dark: color || '#000000',
      light: '#ffffff00'
    }
  });

  const img = new window.Image();
  img.src = dataUrl;
  await img.decode();
  Object.freeze(img);

  qrCache.set(key, img);
  return img;
};



const QRCodeGroup = ({
  obj,
  activeSurface,
  refs,
  handlers,
  updateObject
}) => {
  const [qrImg, setQrImg] = React.useState(() => {
    // try immediate cache hit to avoid flicker
    const key = buildQrCacheKey(obj, activeSurface);

    return qrCache.get(key) || null;
  });

  const cacheKey = React.useMemo(
    () => buildQrCacheKey(obj, activeSurface),
    [obj.qrValue, obj.qrColor, obj.qrBackground, obj.qrQuietZone, activeSurface]
  );
  React.useEffect(() => {
    let cancelled = false;

    (async () => {
      const img = await getQrImage(
        cacheKey,
        obj.qrValue,
        obj.qrColor
      );
      // console.log(
      //   'QR render',
      //   obj.id,
      //   cacheKey,
      //   qrImg instanceof HTMLImageElement
      // );
      if (!cancelled) {
        setQrImg(prev => prev === img ? prev : img);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cacheKey]);

  if (!qrImg) {
    return (
      <Group
        x={obj.x}
        y={obj.y}
        width={obj.width}
        height={obj.height}
        opacity={0}   // invisible but present
      />
    );
  }

  return (
    <Group
      x={obj.x}
      y={obj.y}
      rotation={obj.rotation || 0}
      draggable={obj.draggable !== false}
      opacity={obj.opacity ?? 1}
      ref={(node) => (refs[obj.id] = node)}
      onClick={(e) => handlers.onSelect(e, obj.id)}
      onDragEnd={(e) => {
        updateObject(obj.id, {
          x: e.target.x(),
          y: e.target.y()
        });
      }}
      onTransformEnd={(e) => {
        const node = e.target;
        const scale = node.scaleX();

        node.scaleX(1);
        node.scaleY(1);

        const newSize = Math.max(40, obj.width * scale);

        updateObject(obj.id, {
          width: newSize,
          height: newSize
        });
      }}
    >
      {/* Background */}
      <Rect
        width={obj.width}
        height={obj.height}
        fill={obj.qrBackground || '#ffffff'}
      />

      {/* QR Image */}
      <KonvaImage
        key={cacheKey}   // 🔑 force recreation after surface change
        name="qr-image"   // 👈 ADD THIS
        image={qrImg}
        x={obj.qrQuietZone || 0}
        y={obj.qrQuietZone || 0}
        width={obj.width - (obj.qrQuietZone || 0) * 2}
        height={obj.height - (obj.qrQuietZone || 0) * 2}
        listening={false}
      />
    </Group>
  );
};


  function silverPreviewColor(baseColor, density = 1.0) {
  // Clamp
  const d = Math.max(0, Math.min(1, density));

  // Simple linear scale toward white
  const v = Math.round(255 * (1 - d));
  return `rgb(${v}, ${v}, ${v})`;
}


const DesignerStage = ({ isMenuOpen }) => {
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  const shapeRefs = useRef({});
  const transformerRef = useRef(null);
  const {
    loadFonts,
    selectedIds,
    setSelectedIds,
    toggleSelectedId,
    gridSize,
    isSnapToGridEnabled,
    objects,
    activeSurface,
    updateObject,
    scale,
    setScale,
    cardSettings,
    clearSelection,
    undo,
    redo,
    deleteSelected,
    copySelected,
    cutSelected,
    pasteClipboard,
    selectAll,
    nudgeSelected,
    showGrid,
    addObject,
    addImage,
    // showPlaceholderModal,
    // setShowPlaceholderModal,
    resizeBackgroundsToCard,
    previewSrc,
    setPreviewSrc,
    fitMode,
    setFitMode,
    stageKey,
    objectsFront,
    objectsFrontSilver,
    objectsBack,
    objectsBackSilver,
    setFlushTransforms,
    setStageRef
  } = useDesignerStore();


  const [selectionBox, setSelectionBox] = useState(null);
  const [selectionStart, setSelectionStart] = useState(null);
  const [viewport, setViewport] = useState({width:0,height:0});
  const canvasDisplayWidth = viewport.width;
  const canvasDisplayHeight = viewport.height;

  const { cardWidth, cardHeight} = getStageDimensions(cardSettings);


  const isSilverSurface =
    activeSurface === 'frontSilver' ||
    activeSurface === 'backSilver';


  const commitLiveTransforms = () => {
    if (!transformerRef.current) return;

    const nodes = transformerRef.current.nodes();
    if (!nodes.length) return;

    nodes.forEach(node => {
      const id = node.id();
      if (!id) return;

      const scaleX = node.scaleX();
      const scaleY = node.scaleY();

      const next = {
        x: node.x(),
        y: node.y(),
        rotation: Math.round(node.rotation() * 100) / 100,
      };

      // Always flatten scale if present
      if (scaleX !== 1 || scaleY !== 1) {
        next.width = Math.max(1, node.width() * scaleX);
        next.height = Math.max(1, node.height() * scaleY);
        node.scaleX(1);
        node.scaleY(1);
      }

      updateObject(id, next);
    });
  };

  useEffect(() => {
    setStageRef(stageRef);
  }, []);

  useEffect(() => {
    loadFonts();
  }, []);





  useEffect(() => {
    setFlushTransforms(() => commitLiveTransforms);

    return () => {
      setFlushTransforms(null);
    };
  }, [setFlushTransforms]);


  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const measure = () => setViewport({width:element.clientWidth,height:element.clientHeight});
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(element);
    window.addEventListener('resize',measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize',measure); };
  }, []);

  useEffect(() => {
    if (!cardWidth || !cardHeight || !canvasDisplayWidth || !canvasDisplayHeight) return;
    setScale(fitDesignerScale(cardWidth,cardHeight,canvasDisplayWidth,canvasDisplayHeight));
  }, [cardWidth,cardHeight,canvasDisplayWidth,canvasDisplayHeight,setScale]);

  useEffect(() => {
    if (cardWidth && cardHeight) resizeBackgroundsToCard(cardWidth,cardHeight);
  }, [cardWidth,cardHeight,resizeBackgroundsToCard]);





  useEffect(() => {
    if (!transformerRef.current) return;
    const nodes = selectedIds.map(id => shapeRefs.current[id]).filter(Boolean);
    transformerRef.current.nodes(nodes);
    transformerRef.current.getLayer()?.batchDraw();
  }, [selectedIds, objects, stageKey]);

  const handleSelect = (e, id) => {
    const isShiftPressed = e.evt.shiftKey;

    if (isShiftPressed) {
      toggleSelectedId(id);
    } else {
      setSelectedIds([id]);
    }
  };



  useEffect(() => {
    const handleKeyDown = (e) => {
      const isInputActive = ['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName);

      if (isInputActive) return; // ✅ Skip global shortcuts if typing in a field


      const nudgeStep = e.shiftKey ? 10 : 1;

      if (e.key === 'Delete' && selectedIds.length > 0) {
        deleteSelected();
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        undo();
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        redo();
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
        e.preventDefault();
        copySelected();
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'x') {
        e.preventDefault();
        cutSelected();
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
        e.preventDefault();
        pasteClipboard();
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        selectAll();
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        nudgeSelected(0, -nudgeStep);
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        nudgeSelected(0, nudgeStep);
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        nudgeSelected(-nudgeStep, 0);
      }

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        nudgeSelected(nudgeStep, 0);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedIds, deleteSelected, undo, redo, copySelected, cutSelected, pasteClipboard]);

  const computeImageLayout = (mode, imgWidth, imgHeight, boxWidth, boxHeight) => {
    if (mode === 'stretch') {
      return { width: boxWidth, height: boxHeight, offsetX: 0, offsetY: 0 };
    }

    const aspectImg = imgWidth / imgHeight;
    const aspectBox = boxWidth / boxHeight;

    let width, height;
    if ((mode === 'fit' && aspectImg > aspectBox) || (mode === 'fill' && aspectImg < aspectBox)) {
      width = boxWidth;
      height = width / aspectImg;
    } else {
      height = boxHeight;
      width = height * aspectImg;
    }

    const offsetX = (boxWidth - width) / 2;
    const offsetY = (boxHeight - height) / 2;
    return { width, height, offsetX, offsetY };
  };

  const downloadDataURL = (dataURL, filename) => {
    const a = document.createElement('a');
    a.href = dataURL;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const exportCurrentSurface = async (
    format = 'png',
    options = {}
  ) => {
    if (!stageRef.current) return null;

    const { returnBlob = false } = options;

    const stage = stageRef.current;
    const exportScale = 1 / scale;

    const dataURL = stage.toDataURL({
      x: 0,
      y: 0,
      width: cardWidth,
      height: cardHeight,
      pixelRatio: exportScale,
      mimeType:
        format === 'jpg' ? 'image/jpeg' : 'image/png',
      quality: format === 'jpg' ? 0.95 : undefined
    });

    // 🔥 DEFAULT BEHAVIOR (unchanged)
    if (!returnBlob) {
      downloadDataURL(
        dataURL,
        `card-${activeSurface}.${format}`
      );
      return;
    }

    // 🔥 NEW: convert to Blob
    const res = await fetch(dataURL);
    const blob = await res.blob();

    return blob;
  };

  const handleMouseDown = (e) => {

    // Ignore right-clicks entirely
    if (e.evt.button !== 0) {
      return;
    }

    const stage = e.target.getStage();
    const clickedOnEmpty = e.target === stage;

    if (clickedOnEmpty) {

      clearSelection();

      const pos = stage.getPointerPosition();
      if (!pos) return;

      setSelectionStart(pos);

      setSelectionBox({
        x: pos.x,
        y: pos.y,
        width: 0,
        height: 0
      });

      return;
    }

    // Shape selection handled elsewhere
  };

  const handleMouseMove = (e) => {
    if (!selectionStart || typeof e.target.getPointerPosition !== 'function') return;

    const { x, y } = e.target.getPointerPosition();
    setSelectionBox({
      x: Math.min(selectionStart.x, x),
      y: Math.min(selectionStart.y, y),
      width: Math.abs(x - selectionStart.x),
      height: Math.abs(y - selectionStart.y),
    });
  };

  const handleMouseUp = (e) => {
    if (selectionBox) {
      // If selection box is too small (click instead of drag), don't select anything
      if (selectionBox.width < 5 && selectionBox.height < 5) {
        setSelectionStart(null);
        setSelectionBox(null);
        return;
      }

      // Standard selection box logic
      const selected = objects.filter(obj => {
        const node = shapeRefs.current[obj.id];
        if (!node) return false;

        const box = node.getClientRect();
        const sel = selectionBox;

        return (
          box.x >= sel.x &&
          box.y >= sel.y &&
          box.x + box.width <= sel.x + sel.width &&
          box.y + box.height <= sel.y + sel.height
        );
      }).map(obj => obj.id);

      setSelectedIds(selected);
    }

    // Reset selection box either way
    setSelectionStart(null);
    setSelectionBox(null);
  };

  const getFittedFontSizeMultiline = ({
    text,
    width,
    height,
    fontFamily = 'Arial',
    maxSize = 48,
    minSize = 8,
    lineHeight = 1.2
  }) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    const wrapText = (ctx, text, maxWidth) => {
      const words = text.split(' ');
      let lines = [];
      let line = '';

      words.forEach((word) => {
        const testLine = line + word + ' ';
        const { width: testWidth } = ctx.measureText(testLine);
        if (testWidth > maxWidth && line) {
          lines.push(line.trim());
          line = word + ' ';
        } else {
          line = testLine;
        }
      });

      if (line) lines.push(line.trim());
      return lines;
    };

    for (let fontSize = maxSize; fontSize >= minSize; fontSize--) {
      context.font = `${fontSize}px ${fontFamily}`;
      const lines = wrapText(context, text, width);
      const totalHeight = lines.length * fontSize * lineHeight;

      if (totalHeight <= height) {
        return fontSize;
      }
    }

    return minSize;
  };



  const handleTransformEnd = (e, obj, handlers) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    node.scaleX(1);
    node.scaleY(1);

    const next = {
      x: node.x(),
      y: node.y(),
      rotation: Math.round(node.rotation() * 100) / 100,
    };

    if (obj.type === 'rect') {
      next.width = Math.max(1, node.width() * scaleX);
      next.height = Math.max(1, node.height() * scaleY);
    }

    if (obj.type === 'text') {

      next.width = Math.max(10, node.width() * scaleX);

      if (obj.textFrameMode !== 'auto') {
        next.height = Math.max(10, node.height() * scaleY);
      }

    }

    if (obj.type === 'ellipse') {

      const meta = obj.meta ?? { radiusX: 60, radiusY: 40, segments: 32 };

      const rx = meta.radiusX * scaleX;
      const ry = meta.radiusY * scaleY;

      const updatedMeta = {
        ...meta,
        radiusX: rx,
        radiusY: ry
      };

      next.meta = updatedMeta;
      next.points = ellipseToPolygonPoints(updatedMeta);

    }

    if (obj.type === 'circle') {
      const basePoints = obj.points ?? [];
      const uniformScale = Math.max(scaleX, scaleY);
      next.points = basePoints.map(p => p * uniformScale);
    }

    // console.log("object.type:", obj.type)
    if (obj.type === 'line') {
      const basePoints = obj.points ?? [0, 0, 120, 0];

      // scale x coords by scaleX, y coords by scaleY (same pattern as polygons)
      next.points = basePoints.map((p, i) =>
        i % 2 === 0 ? p * scaleX : p * scaleY
      );
    }



    if (
      obj.type === 'polygon' ||
      obj.type === 'star' ||
      obj.type === 'arrow'
    ) {
      const basePoints = obj.points ?? [];

      // Find bounding box center
      const xs = basePoints.filter((_, i) => i % 2 === 0);
      const ys = basePoints.filter((_, i) => i % 2 === 1);

      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);

      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;

      next.points = basePoints.map((p, i) => {
        if (i % 2 === 0) {
          return (p - centerX) * scaleX + centerX;
        } else {
          return (p - centerY) * scaleY + centerY;
        }
      });
    }




    updateObject(obj.id, next);   // ⭐ THIS IS THE MISSING LINE
  };

  const renderShape = (obj, refs, handlers) => {
    const density = obj.density ?? 1.0;
    const hasPlaceholder = obj.imagePlaceholder?.previewSrc;
    const finalFill = isSilverSurface
      ? silverPreviewColor(obj.fill || '#000', density)
      : obj.fill;
    const finalStroke = isSilverSurface
      ? silverPreviewColor(obj.stroke || '#000', density)
      : obj.stroke;

    const isBgSelected = obj?.isBackground === true;

      const commonProps = {
        id: obj.id,
        ref: (node) => { refs[obj.id] = node; },
        x: obj.x ?? 0,
        y: obj.y ?? 0,
        rotation: obj.rotation || 0,
        shadowColor: obj.shadowColor,
        shadowBlur: obj.shadowBlur,
        shadowOffsetX: obj.shadowOffsetX,
        shadowOffsetY: obj.shadowOffsetY,
        shadowOpacity: obj.shadowOpacity,
        fill: finalFill,
        opacity: isSilverSurface ? 1 : obj.opacity,

        stroke: obj.stroke,
        strokeWidth: obj.strokeWidth,
        dash: obj.dash,

        draggable: !isBgSelected,
        listening: !isBgSelected,

        onClick: (e) => handlers.onSelect(e, obj.id),
        onDragStart: (e) => {
          e.cancelBubble = true;
          handlers.onSelect(e, obj.id);
        },
        onDragEnd: (e) => {
          handlers.update(obj.id, {
            x: e.target.x(),
            y: e.target.y()
          });
        },

        onTransformEnd: (e) => handleTransformEnd(e, obj, handlers)
      };



    const gradientFillProps = isSilverSurface
      ? {} // 🔒 gradients disabled for silver preview
      : {
          fill:
            obj.fillLinearGradientColorStops &&
            obj.fillLinearGradientColorStops.length >= 4
              ? undefined
              : obj.fill,
          fillLinearGradientStartPoint:
            obj.fillLinearGradientColorStops?.length >= 4
              ? obj.fillLinearGradientStartPoint
              : undefined,
          fillLinearGradientEndPoint:
            obj.fillLinearGradientColorStops?.length >= 4
              ? obj.fillLinearGradientEndPoint
              : undefined,
          fillLinearGradientColorStops:
            obj.fillLinearGradientColorStops?.length >= 4
              ? obj.fillLinearGradientColorStops
              : undefined
        };


    switch (obj.type) {
      case 'rect':

        return (
          <Rect
            {...commonProps}
            width={obj.width}
            height={obj.height}
            fill={finalFill}
            stroke={finalStroke}
            strokeWidth={obj.strokeWidth}
            cornerRadius={obj.cornerRadius || 0}
            {...gradientFillProps}
          />
        );

        case 'circle':
          return (
            <Line
              key={obj.id}
              {...commonProps}
              {...gradientFillProps}
              points={obj.points || []}
              closed
              fill={finalFill}
              stroke={finalStroke}
              strokeWidth={obj.strokeWidth}
              hitStrokeWidth={20}
            />
          );

      case 'ellipse':
        return (
          <Line
            key={obj.id}
            {...commonProps}
            {...gradientFillProps}
            points={obj.points || []}
            closed
            fill={finalFill}
            stroke={finalStroke}
            strokeWidth={obj.strokeWidth}
            hitStrokeWidth={20}
          />
        );

        case 'polygon':
          return (
            <Line
              {...commonProps}
              points={obj.points}
              closed
              fill={finalFill}
              stroke={finalStroke}
              strokeWidth={obj.strokeWidth}
              {...gradientFillProps}
            />
          );

      case 'star':
        return (
          <Line
            {...commonProps}
            points={obj.points}
            closed
            fill={finalFill}
            stroke={finalStroke}
            strokeWidth={obj.strokeWidth}
            {...gradientFillProps}
          />
        );

        case 'photo':
        // console.log("obj:", obj)
          return (
            <PhotoFrame
              key={obj.id}
              obj={obj}
              refCallback={(node) => { refs[obj.id] = node; }}
              onSelect={handlers.onSelect}
              onUpdate={handlers.update}
              isSnap={handlers.isSnap}
              grid={handlers.grid}
            />
          );

      case 'arrow':
        return (
          <Line
            key={obj.id}
            {...commonProps}
            {...gradientFillProps}
            points={obj.points}
            closed
            fill={obj.fill || finalStroke}
            stroke={finalStroke}
            strokeWidth={obj.strokeWidth || 1}
          />
        );
      case 'image':
        return (
          <ImageShape
            key={obj.id}
            obj={obj}
            refCallback={(node) => { refs[obj.id] = node; }}
            onSelect={handlers.onSelect}
            onUpdate={handlers.update}
            isSnap={handlers.isSnap}
            grid={handlers.grid}
            onContextMenu={(e) => {
              e.evt.preventDefault();
              e.cancelBubble = true;
            }}
          />
        );
        case 'line': {
          const pts = obj.points ?? [0, 0, 120, 0];
          const strokeW = obj.strokeWidth ?? 2;

          // Give the line a real transform box height so Transformer doesn't go NaN.
          // This is NOT visible (alpha ~ 0), but it creates a stable clientRect.
          const HIT_H = Math.max(20, strokeW * 6);

          return (
            <Group
              key={obj.id}
              id={obj.id}
              ref={(node) => { refs[obj.id] = node; }}
              x={obj.x ?? 0}
              y={obj.y ?? 0}
              rotation={obj.rotation || 0}
              draggable={!isBgSelected}
              listening={!isBgSelected}
              onClick={(e) => handlers.onSelect(e, obj.id)}
              onDragStart={(e) => {
                e.cancelBubble = true;
                handlers.onSelect(e, obj.id);
              }}
              onDragEnd={(e) => {
                handlers.update(obj.id, { x: e.target.x(), y: e.target.y() });
              }}
              onTransformEnd={(e) => handleTransformEnd(e, obj, handlers)}
            >
              {/* Invisible hitbox (creates non-zero height clientRect for Transformer) */}
              <Rect
                x={Math.min(pts[0], pts[2])}
                y={Math.min(pts[1], pts[3]) - HIT_H / 2}
                width={Math.abs(pts[2] - pts[0]) || 1}
                height={HIT_H}
                fill="rgba(0,0,0,0.001)"
                listening={false}
              />

              {/* Actual line */}
              <Line
                points={pts}
                stroke={finalStroke}
                strokeWidth={strokeW}
                lineCap="round"
                hitStrokeWidth={20}
                perfectDrawEnabled={false}
                dash={obj.dash ?? []}
              />
            </Group>
          );
        }
        case 'qrcode': {
          return (
            <QRCodeGroup
              obj={obj}
              activeSurface={activeSurface}
              refs={refs}
              handlers={handlers}
              updateObject={updateObject}
            />
          );
        }

        case "text": {

          const frameMode = obj.textFrameMode || "fixed";
          const padding = obj.padding ?? 6;

          const innerWidth = Math.max((obj.width || 1) - padding * 2, 1);
          const innerHeight = Math.max((obj.height || 1) - padding * 2, 1);

          let fontSize = obj.fontSize || 24;

          // SHRINK MODE
          if (frameMode === "shrink") {
            fontSize = calculateFontSizeToFitBox({
              text: obj.text || "",
              width: innerWidth,
              height: innerHeight,
              fontFamily: obj.fontFamily || "Arial",
              fontStyle: obj.fontStyle || "normal",
              maxFontSize: obj.fontSize || 48,
              lineHeight: obj.lineHeight || 1.2
            });
          }

          // Measure text height
          const tempText = new Konva.Text({
            text: obj.text || "",
            width: innerWidth,
            fontSize,
            fontFamily: obj.fontFamily || "Arial",
            fontStyle: obj.fontStyle || "normal",
            wrap: "word",
            lineHeight: obj.lineHeight || 1.2
          });

          const textHeight = tempText.height();

          // Vertical alignment
          let yOffset = 0;

          if (frameMode !== "auto" && innerHeight > textHeight) {

            const vAlign = obj.verticalAlign || "top";

            if (vAlign === "middle")
              yOffset = (innerHeight - textHeight) / 2;

            if (vAlign === "bottom")
              yOffset = innerHeight - textHeight;

          }

          const textOrigin = getTextOrigin(obj, padding, yOffset);

          return (
            <Text
              {...commonProps}

              x={textOrigin.x}
              y={textOrigin.y}

              text={obj.text || ""}

              width={innerWidth}

              height={frameMode === "auto" ? undefined : innerHeight}

              fontSize={fontSize}
              fontFamily={obj.fontFamily || "Arial"}
              fontStyle={obj.fontStyle || "normal"}
              textDecoration={obj.textDecoration}

              align={obj.align || "left"}

              lineHeight={obj.lineHeight || 1.2}

              fill={finalFill}
              opacity={obj.opacity ?? 1}

              wrap="word"

              stroke="transparent"
              strokeWidth={1}
              hitStrokeWidth={10}
            />
          );

        }

      default:
        return null;
    }
  };



  return (
    <div
      id="konva-container"
      ref={containerRef}
      style={{
        width: "100%",
        minWidth: 0,
        height: "100%",
        minHeight: 0,
        overflowX: 'auto',
        overflowY: 'auto',
        display: 'block',
        backgroundColor: "#e6e4de",
        padding: 0,
        margin: '0 auto'
      }}
    >
      <div
        style={{
          width: cardWidth * scale,
          height: cardHeight* scale,
          marginTop: '15px',
          marginLeft: '15px',
          transition: 'width 0.2s, height 0.2s'
        }}
      >
      <Stage
        ref={stageRef}
        key={stageKey}
        width={cardWidth * scale}
        height={cardHeight * scale}
        scaleX={scale}
        scaleY={scale}
        style={{
          background: "#fff",
          borderRadius: "6px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
        }}

        onContextMenu={(e) => {
          e.evt.preventDefault();
        }}

        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >

        {showGrid && (
          <Layer>
            {Array.from({ length: Math.ceil(cardWidth / gridSize) + 1 }, (_, i) => (
              <Line
                key={`v-${i}`}
                points={[i * gridSize, 0, i * gridSize, cardHeight]}
                stroke="rgba(0,0,0,0.45)"
                listening={false}
              />
            ))}
            {Array.from({ length: Math.ceil(cardHeight/ gridSize) + 1 }, (_, i) => (
              <Line
                key={`h-${i}`}
                points={[0, i * gridSize, cardWidth, i * gridSize]}
                stroke="rgba(0,0,0,0.45)"
                listening={false}
              />
            ))}
          </Layer>
          )}
          <Layer>
            <Group
              x={0}
              y={0}
              width={cardWidth}
              height={cardHeight}
              clipFunc={(ctx) => {
                ctx.rect(0, 0, cardWidth, cardHeight);
              }}
            >
              {Array.isArray(objects) &&
                objects.map(obj =>
                  renderShape(obj, shapeRefs.current, {
                    update: updateObject,
                    onSelect: handleSelect,
                    isSnap: isSnapToGridEnabled,
                    grid: gridSize
                  })
                )
              }

              {selectedIds.length > 0 && (
                <Transformer

                  ref={transformerRef}
                  rotateEnabled
                  centeredScaling={false}
                  ignoreStroke
                  enabledAnchors={
                      selectedIds.length === 1 &&
                      objects.find(o => o.id === selectedIds[0])?.type === 'line'
                        ? ['middle-left', 'middle-right']   // ⭐ ONLY THESE
                        : undefined
                    }
                  boundBoxFunc={(oldBox, newBox) => {
                    const node = transformerRef.current.nodes()[0];
                    if (!node) return newBox;

                    const obj = objects.find(o => o.id === node.id());

                    // ⭐ Special case for line — keep height stable
                    if (obj?.type === 'line') {
                      return {
                        ...newBox,
                        height: oldBox.height,
                      };
                    }

                    const keepRatioTypes = ['polygon', 'star', 'circle'];
                    const anchor = transformerRef.current.getActiveAnchor();

                    const isCorner =
                      anchor?.includes('left') && anchor?.includes('top') ||
                      anchor?.includes('right') && anchor?.includes('top') ||
                      anchor?.includes('left') && anchor?.includes('bottom') ||
                      anchor?.includes('right') && anchor?.includes('bottom');

                    // ⭐ ONLY lock ratio if NOT rotated
                    if (
                      keepRatioTypes.includes(obj?.type) &&
                      isCorner &&
                      Math.abs(node.rotation()) < 0.01
                    ) {
                      const size = Math.max(newBox.width, newBox.height);
                      return { ...newBox, width: size, height: size };
                    }

                    return newBox;
                  }}
                />
              )}
            </Group>
          </Layer>


        </Stage>
      </div>
    </div>
  );
};

export default DesignerStage;
